/**
 * Represents a skin tone option for character avatars.
 *
 * Each skin tone has a unique identifier and a hexadecimal color value
 * that defines the base color to be applied to the character's skin
 * in the avatar rendering system.
 *
 * @property {number} id - Unique identifier for the skin tone option
 * @property {string} value - Hexadecimal color code (e.g., '#FEC79A' for a peach tone)
 */
export type SkinTone = {
  id: number;
  value: string;
};

/**
 * Collection of available skin tones for character avatars.
 *
 * This array defines all the standard skin tone options that can be selected
 * for character avatars in the system. The colors include both realistic human
 * skin tones (IDs 1-5) and fantasy/special skin colors (IDs 6-10), providing
 * a diverse range of options for character customization.
 *
 * The skin tone IDs are used by the character rendering engine to determine which
 * base color to apply to the character's skin, and they correspond to the
 * `currentSkin` prop in the Character component.
 *
 * Color Palette:
 * - Human Tones:
 *   - Deep Rose (#BB8485) - ID: 1
 *   - Tan (#E6A68D) - ID: 2
 *   - Peach (#FEC79A) - ID: 3
 *   - Light Peach (#FEd0BA) - ID: 4
 *   - Pale (#FEEAD3) - ID: 5
 *
 * - Fantasy/Special Tones:
 *   - Light Blue (#9CE2FD) - ID: 6
 *   - Mint Green (#B5FEC9) - ID: 7
 *   - Pale Yellow (#FFF0A3) - ID: 8
 *   - Pink (#FEC3DF) - ID: 9
 *   - Lavender (#C5C6FD) - ID: 10
 *
 */
export const skinTones: SkinTone[] = [
  // Human skin tones
  { id: 1, value: '#BB8485' }, // Deep Rose
  { id: 2, value: '#E6A68D' }, // Tan
  { id: 3, value: '#FEC79A' }, // Peach
  { id: 4, value: '#FEd0BA' }, // Light Peach
  { id: 5, value: '#FEEAD3' }, // Pale

  // Fantasy/Special skin tones
  { id: 6, value: '#9CE2FD' }, // Light Blue
  { id: 7, value: '#B5FEC9' }, // Mint Green
  { id: 8, value: '#FFF0A3' }, // Pale Yellow
  { id: 9, value: '#FEC3DF' }, // Pink
  { id: 10, value: '#C5C6FD' }, // Lavender
];
